import { MaskLine, Rise } from "@/components/motion/Reveal";
import { PRACTICE_AREAS } from "@/data/site";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import Seo from "@/seo/Seo";
import { ldBreadcrumb } from "@/seo/seoConfig";

export default function PracticePage() {
  const { t } = useLang();
  return (
    <div className="pt-40">
      <Seo
        title="Practice Areas · Cyber Crime · Economic Offences · CFD Trading Disputes · VARA · PDPL"
        description="Fifteen specialisms led by Cyber Crime & Digital Fraud: economic offences and white-collar defence, CFD, forex and online trading disputes, Virtual Assets & VARA, Data Privacy & Cybersecurity, AI Governance, Capital Markets & FinTech, Corporate & Commercial, India-Entry & Cross-Border, DIFC & ADGM advisory."
        path="/practice-areas"
        keywords={["cyber crime lawyer Dubai", "cyber crime lawyer UAE", "online fraud lawyer Dubai", "crypto scam recovery lawyer UAE", "economic offences lawyer", "white collar crime lawyer Dubai", "EOW lawyer Pune", "CFD trading dispute lawyer UAE", "forex broker dispute lawyer Dubai", "broker withdrawal refusal lawyer", "SCA complaint lawyer", "VARA lawyer Dubai", "UAE PDPL lawyer", "DIFC lawyer", "ADGM lawyer", "AI governance lawyer UAE", "fintech lawyer Dubai", "corporate lawyer Dubai", "DPDPA compliance lawyer", "cyber security lawyer Dubai"]}
        jsonLd={ldBreadcrumb([{ name: "Home", path: "/" }, { name: "Practice Areas", path: "/practice-areas" }])}
      />
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <Rise><div className="flex items-center gap-4 mb-10"><span className="accent-bar" /><span className="eyebrow">{t.practicePage.eyebrow}</span></div></Rise>
        <h1 className="font-serif tracking-tight leading-[0.9] text-[13vw] lg:text-[8vw]">
          <MaskLine delay={0.15}>{t.practicePage.title1}</MaskLine>
          <MaskLine delay={0.32}><span className="italic-accent">{t.practicePage.title2}</span></MaskLine>
        </h1>
        <Rise delay={0.5}>
          <p className="mt-10 text-[#A19D94] text-lg max-w-2xl leading-relaxed">{t.practicePage.lead}</p>
        </Rise>
      </section>

      <section className="mt-24 border-y border-[#141414]">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          {PRACTICE_AREAS.map((p, i) => (
            <Rise key={p.n} delay={i * 0.02}>
              <div data-testid={`practice-area-${p.n}`} className={`pa-row grid grid-cols-12 gap-6 items-start py-10 border-b border-[#141414] last:border-0 group ${p.featured ? "relative" : ""}`}>
                {p.featured && <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#C84C35]" aria-hidden="true" />}
                <div className={`col-span-2 lg:col-span-1 chapter-num pt-2 ${p.featured ? "text-[#C84C35] pl-4 lg:pl-0" : ""}`}>{p.n}</div>
                <div className="col-span-10 lg:col-span-5">
                  <Link to={`/practice-areas/${p.slug}`} data-testid={`practice-link-${p.slug}`} className="block group/title">
                    <h3 className="pa-title font-serif text-4xl lg:text-6xl leading-[1] tracking-tight group-hover/title:text-[#C84C35] transition-colors duration-500">{p.title}</h3>
                  </Link>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {p.featured && <span data-testid="practice-featured-tag" className="chapter-num bg-[#C84C35] text-[#050505] px-2.5 py-1">{t.practice.featured}</span>}
                    {p.tags.map((tag) => (
                      <span key={tag} className="chapter-num border border-[#222] px-2.5 py-1">{tag}</span>
                    ))}
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-5 text-[#A19D94] text-base lg:text-lg leading-relaxed">
                  {p.desc}
                  <Link to={`/practice-areas/${p.slug}`} className="chapter-num mt-4 inline-flex items-center gap-2 text-[#F4F1EB] link-hover">{t.practiceDetail.explore} <ArrowUpRight size={12} strokeWidth={1.5} /></Link>
                </div>
                <div className="col-span-12 lg:col-span-1 flex lg:justify-end text-[#66635C] group-hover:text-[#C84C35] transition-colors">
                  <Link to={`/practice-areas/${p.slug}`} aria-label={p.title}><ArrowUpRight size={22} strokeWidth={1.25} /></Link>
                </div>
              </div>
            </Rise>
          ))}
        </div>
      </section>

      <section className="py-32 lg:py-48">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Rise>
              <h2 className="font-serif text-5xl lg:text-[7vw] leading-[0.95] tracking-tight">
                {t.practicePage.close.split(" ").map((w, i) => (
                  <span key={i} className={i === 1 ? "italic-accent" : ""}>{w} </span>
                ))}
              </h2>
            </Rise>
          </div>
          <div className="lg:col-span-4 lg:justify-end flex">
            <Rise delay={0.15}><Link to="/contact" className="btn-primary">{t.contactCTA.cta} <ArrowUpRight size={14} strokeWidth={1.5} /></Link></Rise>
          </div>
        </div>
      </section>
    </div>
  );
}
